import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { HERO, STATS } from "@/data/content";
import { MaskLine } from "./Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden" data-testid="hero">
      {/* textured parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-10"
      >
        <div
          className="h-[130%] w-full bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1527049979667-990f1d0d8e7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)",
          }}
        />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/85 to-[#0A0A0A]" />
      <div className="absolute inset-0 -z-10 grain" />
      {/* accent glow */}
      <div className="pointer-events-none absolute -right-40 top-24 -z-10 h-[520px] w-[520px] rounded-full bg-[#D4F870]/10 blur-[140px]" />

      <div className="ec-container flex min-h-screen flex-col justify-center pt-28 pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="ec-eyebrow mb-8"
          data-testid="hero-eyebrow"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#D4F870]" />
          {HERO.eyebrow}
        </motion.p>

        <h1 className="font-display text-[13vw] font-500 leading-[0.92] tracking-tight text-[#F3F4F6] sm:text-[10vw] md:text-[8vw] lg:text-[7.2rem]">
          {HERO.lines.map((line, i) => (
            <MaskLine key={i} delay={0.35 + i * 0.12}>
              {i === HERO.lines.length - 1 ? (
                <span className="text-[#D4F870]">{line}</span>
              ) : (
                line
              )}
            </MaskLine>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex max-w-2xl flex-col gap-8"
        >
          <p className="text-base leading-relaxed text-[#9CA3AF] md:text-lg" data-testid="hero-sub">
            {HERO.sub}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="/welcome"
              className="group inline-flex items-center gap-2 rounded-full bg-[#D4F870] px-7 py-3.5 text-sm font-medium text-[#0A0A0A] transition-colors hover:bg-[#BEF264]"
              data-testid="hero-primary-cta"
            >
              {HERO.primary}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm text-[#F3F4F6] transition-colors hover:border-white/40"
              data-testid="hero-secondary-cta"
            >
              Fiyatları Gör
            </a>
          </div>
        </motion.div>
      </div>

      {/* bottom stats strip */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-[#0A0A0A]/40 backdrop-blur-sm"
      >
        <div className="ec-container grid grid-cols-2 divide-x divide-white/10 md:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 + i * 0.1, duration: 0.6 }}
              className="px-4 py-6 md:px-6"
              data-testid={`hero-stat-${i}`}
            >
              <div className="font-display text-2xl text-[#F3F4F6] md:text-3xl">{s.value}</div>
              <div className="mt-1 text-xs text-[#9CA3AF]">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
